import './QuizElement.less'

import { ButtonOutline } from 'front-end/src/components'
import { TopicElementModelAsEditor } from 'front-end/src/graphql/private/PrivateSchemaTypes'
import React, { useContext, useEffect, useState } from 'react'

import { t, Trans } from '@lingui/macro'

import {
  IOptionResponse,
  IQuizResponse,
  ITopicInTopicStore
} from '../../../../../types/Topic'
import { TopicDetailContext } from '../TopicDetailContext'
import { FreeQuizNote } from './quiz-components/FreeQuizNote'
import { QuizElementWrapper } from './quiz-components/QuizElementWrapper'
import { QuizOption } from './quiz-components/QuizOption'
import { QuizResult } from './quiz-components/QuizResult'
import { QuizSaveAnswer } from './quiz-components/QuizSaveAnswer'
import * as quizUtils from './utils'

export const PersistedQuizElement = (
  props: TopicElementModelAsEditor & {
    allowRetry: boolean
    quizResponses: ITopicInTopicStore['enrolmentOrSelfEnrol']['quizResponses']
  }
) => {
  const { topic, saveFreeQuizResponse, saveQuizResponse } =
    useContext(TopicDetailContext)
  const [selectedOptions, setSelectedOptions] = useState<number[]>([])
  const [savedResponse, setSavedResponse] = useState<IQuizResponse | null>(null)
  const [freeQuizResponse, setFreeQuizResponse] = useState('')
  const [freeQuizAnswered, setFreeQuizAnswered] = useState(false)

  const {
    type,
    quizOptions,
    id: elementId,
    data,
    allowRetry,
    quizResponses
  } = props

  const showSubmitOption =
    !topic.deleted_at &&
    quizUtils.showSubmitOption(selectedOptions, savedResponse)
  const isMultiChoice = quizUtils.isMultiChoice(type)
  const isOpenChoice = quizUtils.isOpenChoice(type)
  const isFreeChoice = quizUtils.isFreeChoice(type)

  useEffect(() => {
    const quizResponse = quizResponses.find(
      ({ topic_element_id: element_id }) => props.id === element_id
    )

    if (!quizResponse) {
      setSavedResponse(null) // this can occur when enrolment is restarted-our response will not be found in the topic store
      setFreeQuizAnswered(false)
      !freeQuizResponse && setFreeQuizResponse('')
      return
    }

    setSavedResponse(quizResponse as IQuizResponse)
    setFreeQuizResponse(quizResponse.freeQuizResponse || '')
    setFreeQuizAnswered(!!quizResponse)
  }, [props])

  return (
    <QuizElementWrapper element={props}>
      {isFreeChoice ? (
        <>
          <textarea
            className="QuizElement__answer__free form-textAreaInput"
            id={`element-${props.type}-${props.id}`}
            placeholder={t`Your response...`}
            disabled={freeQuizAnswered}
            value={freeQuizResponse}
            onChange={(e) => {
              setFreeQuizResponse(e.target.value)
            }}
          />
          <FreeQuizNote
            currentLength={freeQuizResponse.length}
            minLength={data.minLength}
            maxLength={data.maxLength}
          />
          {freeQuizAnswered ? (
            <div className="QuizElement__submit__wrapper">
              <ButtonOutline isDisabled>
                <Trans>Answered</Trans>
              </ButtonOutline>
            </div>
          ) : (
            <QuizSaveAnswer
              disabled={
                !!topic.deleted_at ||
                freeQuizAnswered ||
                freeQuizResponse.length < data.minLength ||
                freeQuizResponse.length > data.maxLength
              }
              onSave={async () => {
                setSavedResponse(
                  await saveFreeQuizResponse(elementId, freeQuizResponse)
                )

                setFreeQuizAnswered(true)
              }}
            />
          )}
        </>
      ) : (
        quizOptions.map((option) => {
          let isSelected = selectedOptions.includes(option.id)
          let existingOptionResponse: IOptionResponse | undefined
          let optionClickHandler = () => {}
          if (savedResponse && savedResponse.optionResponses) {
            const optionResponse = savedResponse.optionResponses.find(
              ({ option_id }) => option_id === option.id
            )
            if (optionResponse) {
              existingOptionResponse = optionResponse
              isSelected = optionResponse.value
            }
          }

          if (!existingOptionResponse) {
            optionClickHandler = () => {
              if (isSelected) {
                setSelectedOptions(
                  selectedOptions.filter((id) => {
                    return id !== option.id
                  })
                )
              } else {
                if (isMultiChoice || isOpenChoice) {
                  setSelectedOptions(selectedOptions.concat([option.id]))
                } else {
                  setSelectedOptions([option.id])
                }
              }
            }
          }

          return (
            <QuizOption
              key={option.id}
              {...{
                option,
                existingOptionResponse,
                isSelected,
                isMultiChoice,
                isOpen: isOpenChoice,
                quizResponse: savedResponse,
                optionClickHandler,
                allowRetry
              }}
            />
          )
        })
      )}
      {showSubmitOption && (
        <QuizSaveAnswer
          disabled={!showSubmitOption}
          onSave={async () => {
            const optionResponses = quizOptions.map(({ id }) => {
              return {
                value: selectedOptions.includes(id),
                option_id: id
              }
            })
            setSavedResponse(await saveQuizResponse(elementId, optionResponses))
          }}
        />
      )}
      <QuizResult quizResponse={savedResponse} quizType={type} />
    </QuizElementWrapper>
  )
}
